import "./index.css";
import type { IButtonProps } from "@/ui-kit";
import { IconButton, Skeleton, Tooltip, Typography } from "@/ui-kit";
import { ArrowLeftIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useAppStore } from "@/store/app/slice";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/PageHeader/Breadcrumbs";
import { usePageTitleState } from "./PageTitleProvider";
import { useBreakpoints } from "@/hooks/useBreakpoints";

type IProps = {
  title?: ReactNode;
  backUrl?: string;
  backUrlButtonProps?: IButtonProps;
  loading?: boolean;
};

export const PageHeader = ({
  title,
  backUrl,
  backUrlButtonProps,
  loading,
}: IProps) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const navigate = useNavigate();
  const { items } = usePageTitleState();

  const hasContextItems = items.length > 0;

  const contextBackUrl = [...items]
    .sort((a, b) => b.order - a.order)
    .find((item) => item.backUrl)?.backUrl;

  const allLoading = hasContextItems && items.every((item) => item.loading);

  const effectiveBackUrl = hasContextItems ? contextBackUrl : backUrl;
  const effectiveLoading = hasContextItems ? allLoading : loading;

  const handleBackClick = useCallback(() => {
    if (effectiveBackUrl) {
      if (effectiveBackUrl.includes("..")) {
        navigate(effectiveBackUrl, { relative: "path" });
      } else {
        navigate(effectiveBackUrl);
      }
      useAppStore.getState().setSavedUrl(null);
    }
  }, [navigate, effectiveBackUrl]);

  const renderContent = () => {
    if (hasContextItems) {
      return <Breadcrumbs items={items} />;
    }

    if (loading) {
      return <Skeleton width="12.5rem" height="2rem" />;
    }

    return (
      <Typography
        color="text-primary"
        weight="bold"
        as="h3"
        variant={isMobile ? "h4" : "h3"}
        text={title}
      />
    );
  };

  return (
    <div className="page-header">
      {!effectiveLoading && effectiveBackUrl && (
        <Tooltip content={t("action.back")} placement="bottom-start">
          <IconButton
            className="page-header__back-button"
            content={
              <ArrowLeftIcon className="page-header__back-button-icon" />
            }
            variant="ghost"
            color="layer1"
            aria-label={t("action.back")}
            {...backUrlButtonProps}
            onClick={handleBackClick}
          />
        </Tooltip>
      )}
      {renderContent()}
    </div>
  );
};
